import React from 'react';
import {
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface OptionVisaComponentProps {
  optionalServices: {
    EnlevementParis: boolean;
    LivraisonParis: boolean;
    RetourDHL: boolean;
    TraitementVIP: boolean;
    SaisieFormulaire: boolean;
  };
  setOptionalServices: React.Dispatch<
    React.SetStateAction<{
      EnlevementParis: boolean;
      LivraisonParis: boolean;
      RetourDHL: boolean;
      TraitementVIP: boolean;
      SaisieFormulaire: boolean;
    }>
  >;
}

/**
 * Composant permettant de sélectionner différents services optionnels.
 * Chaque case à cocher modifie le state dans le parent (via setOptionalServices).
 */
const OptionVisaComponent: React.FC<OptionVisaComponentProps> = ({
  optionalServices,
  setOptionalServices,
}) => {
  // Les montants associés à chaque service
  const SERVICE_PRICES = {
    EnlevementParis: 49,   // Enlèvement Paris
    LivraisonParis: 49,    // Livraison Paris
    RetourDHL: 49,         // Retour DHL
    TraitementVIP: 49,     // Traitement VIP
    SaisieFormulaire: 39,  // Saisie formulaire
  };

  // Textes explicatifs pour les Tooltips
  const TOOLTIP_TEXTS = {
    EnlevementParis:
      'Enlèvement du dossier dans vos bureaux ou domicile par nos agents (Paris et banlieue limitrophe).',
    LivraisonParis:
      'Livraison du passeport dans vos bureaux ou domicile par nos agents (Paris et banlieue limitrophe).',
    RetourDHL:
      'Expédition du passeport par DHL.',
    SaisieFormulaire:
      'Si vous choisissez cette option, vous n’avez pas à remplir le formulaire indiqué dans la liste des documents à fournir.',
    TraitementVIP:
      'Traitement immédiat du dossier par nos services et procédure accélérée au consulat.',
  };

  // Fonction générique pour mettre à jour l'état lors du clic sur une checkbox
  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    optionName: keyof typeof optionalServices
  ) => {
    setOptionalServices((prev) => ({
      ...prev,
      [optionName]: event.target.checked,
    }));
  };

  // Petit helper pour afficher le label + icône de Tooltip
  const renderLabelWithTooltip = (
    label: string,
    price: number,
    tooltipKey: keyof typeof TOOLTIP_TEXTS
  ) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {`${label} ${price}€`}
      <Tooltip title={TOOLTIP_TEXTS[tooltipKey]} arrow>
        <IconButton size="small" sx={{ ml: 0.5 }}>
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box
      sx={{
        border: '1px solid #ccc',
        borderRadius: 2,
        p: 2,
        mb: 2,
        backgroundColor: '#f9f9f9',
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        Services optionnels
      </Typography>

      {/* ENLÈVEMENT PARIS */}
      <FormControlLabel
        control={
          <Checkbox
            checked={optionalServices.EnlevementParis}
            onChange={(e) => handleCheckboxChange(e, 'EnlevementParis')}
          />
        }
        label={renderLabelWithTooltip(
          'Enlèvement dossier',
          SERVICE_PRICES.EnlevementParis,
          'EnlevementParis'
        )}
      />

      {/* LIVRAISON PARIS */}
      <FormControlLabel
        control={
          <Checkbox
            checked={optionalServices.LivraisonParis}
            onChange={(e) => handleCheckboxChange(e, 'LivraisonParis')}
          />
        }
        label={renderLabelWithTooltip(
          'Livraison passeport',
          SERVICE_PRICES.LivraisonParis,
          'LivraisonParis'
        )}
      />

      {/* RETOUR DHL */}
      <FormControlLabel
        control={
          <Checkbox
            checked={optionalServices.RetourDHL}
            onChange={(e) => handleCheckboxChange(e, 'RetourDHL')}
          />
        }
        label={renderLabelWithTooltip(
          'Retour DHL',
          SERVICE_PRICES.RetourDHL,
          'RetourDHL'
        )}
      />

      {/* SAISIE FORMULAIRE */}
      <FormControlLabel
        control={
          <Checkbox
            checked={optionalServices.SaisieFormulaire}
            onChange={(e) => handleCheckboxChange(e, 'SaisieFormulaire')}
          />
        }
        label={renderLabelWithTooltip(
          'Saisie formulaire',
          SERVICE_PRICES.SaisieFormulaire,
          'SaisieFormulaire'
        )}
      />

      {/* TRAITEMENT VIP */}
      <FormControlLabel
        control={
          <Checkbox
            checked={optionalServices.TraitementVIP}
            onChange={(e) => handleCheckboxChange(e, 'TraitementVIP')}
          />
        }
        label={renderLabelWithTooltip(
          'Traitement VIP',
          SERVICE_PRICES.TraitementVIP,
          'TraitementVIP'
        )}
      />
    </Box>
  );
};

export default OptionVisaComponent;
